<template>
  <div class="menu-icon-picker">
    <div class="menu-icon-picker__current">
      <span class="menu-icon-picker__preview" :title="displayIcon || '无图标'">
        <i v-if="displayIcon" class="fa" :class="displayIcon"></i>
        <i v-else class="el-icon-minus"></i>
      </span>
      <el-input
        :value="value"
        placeholder="如 fa-home，或点击选择"
        clearable
        @input="handleInput"
      />
      <el-button @click="openDialog">选择</el-button>
      <el-button type="text" @click="clearIcon">清空</el-button>
    </div>

    <el-dialog
      :visible.sync="dialogVisible"
      width="520px"
      top="10vh"
      append-to-body
      custom-class="menu-icon-picker-dialog"
      :close-on-click-modal="true"
      :show-close="true"
      @open="handleDialogOpen"
      @closed="handleDialogClosed"
    >
      <div slot="title" class="menu-icon-picker__dialog-title">选择菜单图标</div>

      <div class="menu-icon-picker__toolbar">
        <div class="menu-icon-picker__now">
          <span class="menu-icon-picker__now-preview">
            <i v-if="pendingIcon" class="fa" :class="pendingIcon"></i>
            <i v-else class="el-icon-minus"></i>
          </span>
          <div class="menu-icon-picker__now-meta">
            <span class="menu-icon-picker__now-label">{{ pendingLabel || '未选择' }}</span>
            <span class="menu-icon-picker__now-value">{{ pendingIcon || '—' }}</span>
          </div>
        </div>
        <el-input
          v-model="keyword"
          size="small"
          placeholder="搜索，如 fa-home、首页"
          prefix-icon="el-icon-search"
          clearable
          class="menu-icon-picker__search"
        />
      </div>

      <div v-if="!keyword" class="menu-icon-picker__tabs">
        <button
          v-for="group in MENU_ICON_PRESETS"
          :key="group.name"
          type="button"
          class="menu-icon-picker__tab"
          :class="{ 'is-active': activeGroup === group.name }"
          @click="activeGroup = group.name"
        >
          {{ group.name }}
        </button>
      </div>

      <div class="menu-icon-picker__body">
        <div v-if="visibleItems.length === 0" class="menu-icon-picker__empty">未找到匹配图标</div>
        <div v-else class="menu-icon-picker__grid">
          <button
            v-for="item in visibleItems"
            :key="item.value"
            type="button"
            class="menu-icon-picker__item"
            :class="{ 'is-active': pendingIcon === item.value }"
            :title="item.label + ' (' + item.value + ')'"
            @click="pickIcon(item.value)"
            @dblclick="confirmIcon(item.value)"
          >
            <i class="fa" :class="item.value"></i>
          </button>
        </div>
      </div>

      <div slot="footer" class="menu-icon-picker__footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :disabled="!pendingIcon" @click="confirmIcon()">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/** 菜单常用 Font Awesome 4.7 图标预设。最后更新：2026-08-29 */
export const MENU_ICON_PRESETS = [
  {
    name: '通用',
    items: [
      { value: 'fa-home', label: '首页' },
      { value: 'fa-list', label: '列表' },
      { value: 'fa-th-large', label: '模块' },
      { value: 'fa-sitemap', label: '树形' },
      { value: 'fa-folder', label: '文件夹' },
      { value: 'fa-folder-open', label: '打开' },
      { value: 'fa-star', label: '收藏' },
      { value: 'fa-bell', label: '通知' },
    ],
  },
  {
    name: '用户',
    items: [
      { value: 'fa-users', label: '用户组' },
      { value: 'fa-user', label: '用户' },
      { value: 'fa-user-circle-o', label: '个人' },
      { value: 'fa-address-book', label: '通讯录' },
      { value: 'fa-address-card', label: '身份卡' },
      { value: 'fa-id-card', label: '证件' },
      { value: 'fa-lock', label: '权限' },
      { value: 'fa-key', label: '密钥' },
      { value: 'fa-shield', label: '安全' },
    ],
  },
  {
    name: '配置',
    items: [
      { value: 'fa-cog', label: '配置' },
      { value: 'fa-cogs', label: '系统' },
      { value: 'fa-sliders', label: '参数' },
      { value: 'fa-wrench', label: '维护' },
      { value: 'fa-history', label: '历史' },
      { value: 'fa-bug', label: '调试' },
      { value: 'fa-flask', label: '测试' },
      { value: 'fa-sort-amount-asc', label: '排序' },
    ],
  },
  {
    name: '数据',
    items: [
      { value: 'fa-line-chart', label: '折线' },
      { value: 'fa-bar-chart', label: '柱状' },
      { value: 'fa-pie-chart', label: '饼图' },
      { value: 'fa-area-chart', label: '面积' },
      { value: 'fa-table', label: '表格' },
      { value: 'fa-file-text-o', label: '文档' },
      { value: 'fa-file-word-o', label: 'Word' },
      { value: 'fa-diamond', label: '报表' },
    ],
  },
  {
    name: '校园',
    items: [
      { value: 'fa-graduation-cap', label: '教务' },
      { value: 'fa-book', label: '图书' },
      { value: 'fa-calendar', label: '日程' },
      { value: 'fa-cutlery', label: '食堂' },
      { value: 'fa-building', label: '楼宇' },
      { value: 'fa-car', label: '车辆' },
      { value: 'fa-video-camera', label: '监控' },
      { value: 'fa-microphone', label: '话机' },
    ],
  },
  {
    name: '设备',
    items: [
      { value: 'fa-desktop', label: '大屏' },
      { value: 'fa-laptop', label: '电脑' },
      { value: 'fa-mobile', label: '手机' },
      { value: 'fa-tablet', label: '平板' },
      { value: 'fa-server', label: '服务器' },
      { value: 'fa-wifi', label: '网络' },
      { value: 'fa-hdd-o', label: '存储' },
      { value: 'fa-exchange', label: '接入' },
    ],
  },
  {
    name: '第三方',
    items: [
      { value: 'fa-puzzle-piece', label: '第三方' },
      { value: 'fa-share-alt', label: '对接' },
      { value: 'fa-cloud', label: '云端' },
      { value: 'fa-link', label: '链接' },
      { value: 'fa-external-link', label: '外链' },
      { value: 'fa-weixin', label: '微信' },
      { value: 'fa-qq', label: 'QQ' },
      { value: 'fa-comments-o', label: '消息' },
    ],
  },
];

const iconLabelMap = {};
MENU_ICON_PRESETS.forEach(group => {
  group.items.forEach(item => {
    iconLabelMap[item.value] = item.label;
  });
});

export default {
  name: 'MenuIconPicker',
  props: {
    value: { type: String, default: '' },
  },
  data() {
    return {
      MENU_ICON_PRESETS,
      keyword: '',
      dialogVisible: false,
      pendingIcon: '',
      activeGroup: MENU_ICON_PRESETS[0].name,
    };
  },
  computed: {
    displayIcon() {
      return this.normalizeIcon(this.value);
    },
    pendingLabel() {
      return iconLabelMap[this.pendingIcon] || '';
    },
    visibleItems() {
      const keyword = (this.keyword || '').trim().toLowerCase();
      if (keyword) {
        const result = [];
        MENU_ICON_PRESETS.forEach(group => {
          group.items.forEach(item => {
            if (item.label.toLowerCase().indexOf(keyword) >= 0
              || item.value.toLowerCase().indexOf(keyword) >= 0) {
              result.push(item);
            }
          });
        });
        return result;
      }

      const group = MENU_ICON_PRESETS.find(g => g.name === this.activeGroup);
      return group ? group.items : [];
    },
  },
  methods: {
    normalizeIcon(raw) {
      const icon = (raw || '').trim();
      if (!icon) return '';
      if (icon.indexOf('fa-') === 0) return icon;
      if (icon.indexOf('fa ') === 0) return icon.replace(/^fa\s+/, '');
      return 'fa-' + icon.replace(/^fa-/, '');
    },
    handleInput(val) {
      this.$emit('input', val || '');
    },
    openDialog() {
      this.dialogVisible = true;
    },
    handleDialogOpen() {
      this.keyword = '';
      this.pendingIcon = this.displayIcon;
      this.activeGroup = this.resolveGroupByIcon(this.pendingIcon) || MENU_ICON_PRESETS[0].name;
    },
    handleDialogClosed() {
      this.keyword = '';
      this.pendingIcon = '';
    },
    resolveGroupByIcon(icon) {
      if (!icon) return '';
      for (let i = 0; i < MENU_ICON_PRESETS.length; i++) {
        const group = MENU_ICON_PRESETS[i];
        if (group.items.some(item => item.value === icon)) {
          return group.name;
        }
      }
      return '';
    },
    pickIcon(iconValue) {
      this.pendingIcon = iconValue;
    },
    confirmIcon(iconValue) {
      const next = this.normalizeIcon(iconValue || this.pendingIcon);
      if (!next) return;
      this.$emit('input', next);
      this.dialogVisible = false;
    },
    clearIcon() {
      this.$emit('input', '');
    },
  },
};
</script>

<style scoped>
.menu-icon-picker {
  width: 100%;
}

.menu-icon-picker__current {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

.menu-icon-picker__preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  color: #606266;
  flex-shrink: 0;
  line-height: 0;
}

.menu-icon-picker__preview .fa,
.menu-icon-picker__preview .el-icon-minus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  width: 1em;
  height: 1em;
}

.menu-icon-picker__current .el-input {
  flex: 1;
}

.menu-icon-picker__current .el-button--text {
  padding-left: 8px;
  padding-right: 8px;
}

.menu-icon-picker__dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.menu-icon-picker__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.menu-icon-picker__now {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 148px;
  padding: 8px 12px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #f8fafc;
}

.menu-icon-picker__now-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e4e7ed;
  color: var(--btn-primary, #409eff);
  flex-shrink: 0;
}

.menu-icon-picker__now-preview .fa,
.menu-icon-picker__now-preview .el-icon-minus {
  font-size: 20px;
  line-height: 1;
}

.menu-icon-picker__now-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.menu-icon-picker__now-label {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
}

.menu-icon-picker__now-value {
  font-size: 12px;
  color: #909399;
  font-family: Consolas, Monaco, monospace;
  line-height: 1.2;
}

.menu-icon-picker__search {
  flex: 1;
}

.menu-icon-picker__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.menu-icon-picker__tab {
  padding: 4px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 999px;
  background: #fff;
  color: #606266;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.menu-icon-picker__tab:hover {
  color: var(--btn-primary, #409eff);
  border-color: #c6e2ff;
  background: #f5f9ff;
}

.menu-icon-picker__tab.is-active {
  color: #fff;
  border-color: var(--btn-primary, #409eff);
  background: var(--btn-primary, #409eff);
}

.menu-icon-picker__body {
  height: 240px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  padding: 8px;
}

.menu-icon-picker__grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 6px;
}

.menu-icon-picker__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  min-height: 44px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: #f5f7fa;
  color: #606266;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.menu-icon-picker__item:hover {
  border-color: #c6e2ff;
  background: #ecf5ff;
  color: var(--btn-primary, #409eff);
}

.menu-icon-picker__item.is-active {
  border-color: var(--btn-primary, #409eff);
  background: #ecf5ff;
  color: var(--btn-primary, #409eff);
  box-shadow: inset 0 0 0 1px rgba(64, 158, 255, 0.2);
}

.menu-icon-picker__item .fa {
  font-size: 18px;
  line-height: 1;
}

.menu-icon-picker__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 13px;
}

.menu-icon-picker__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>

<style>
.menu-icon-picker-dialog.el-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.menu-icon-picker-dialog .el-dialog__header {
  padding: 16px 20px 10px;
  border-bottom: 1px solid #ebeef5;
}

.menu-icon-picker-dialog .el-dialog__body {
  padding: 14px 20px 8px;
}

.menu-icon-picker-dialog .el-dialog__footer {
  padding: 10px 20px 16px;
  border-top: 1px solid #ebeef5;
}
</style>
